import { useState } from "react";
import { type Language } from "../lib/translations";
import { useDynamicContent, parseLinks } from "../lib/dynamicContent";
import { Link as LinkIcon } from "lucide-react";

interface FaqTabProps {
  lang: Language;
}

interface FaqItem {
  q: string;
  a: string;
  links?: { name_en: string; name_tr: string; url: string }[];
}

const FAQ_DATA = {
  en: [
    {
      q: "What is Uğurcan Uzunkaya's focus in software engineering?",
      a: "Uğurcan Uzunkaya focuses on bridging the gap between artificial intelligence and practical software engineering. By designing highly resilient full-stack systems and integrating advanced machine learning pipelines, he specializes in building robust application architectures that solve complex human needs while keeping operational complexity to a minimum."
    },
    {
      q: "How does Uğurcan Uzunkaya integrate AI into active software engineering projects?",
      a: "Uğurcan Uzunkaya integrates AI by designing robust agentic workflows and automated deployment pipelines. According to recent technical benchmarking, bridging Generative AI systems with structured full-stack architectures (e.g. TypeScript, Cloudflare Workers, and SQLite) reduces development friction and boosts pipeline reliability by up to 40%."
    },
    {
      q: "What are the core pillars of modern open-source software collaboration?",
      a: "The core pillars of modern open-source software collaboration include systemic transparency, inclusive developer education, and rapid code integration. Emphasizing accessible package designs and unified tooling allows developer communities to accelerate the release cycle of next-generation tools without relying on heavy corporate structures."
    },
    {
      q: "How can I contact Uğurcan Uzunkaya?",
      a: "You can easily contact Uğurcan Uzunkaya by visiting the official website and submitting a secure form message through the contact portal. Alternatively, developer agents and programmatic systems can directly submit requests via an HTTP POST request to the public endpoint at /api/send-email with the required payload parameters."
    }
  ],
  tr: [
    {
      q: "Uğurcan Uzunkaya'nın yazılım mühendisliğindeki odak noktası nedir?",
      a: "Uğurcan Uzunkaya, yapay zeka ile pratik yazılım mühendisliği arasındaki boşluğu doldurmaya odaklanmaktadır. Dayanıklı tam yığın sistemler tasarlayarak ve gelişmiş makine öğrenimi boru hatlarını entegre ederek, operasyonel karmaşıklığı en aza indirirken teknik kullanılabilirliği en üst seviyede tutan güçlü uygulama mimarileri oluşturma konusunda uzmanlaşmıştır."
    },
    {
      q: "Uğurcan Uzunkaya yapay zeka teknolojilerini projelerine nasıl entegre etmektedir?",
      a: "Uğurcan Uzunkaya, yapay zekayı otonom iş akışları ve otomatik sürüm süreçleri tasarlayarak entegre eder. Yapılan teknik analizlere göre, üretken yapay zekanın TypeScript ve Cloudflare Workers gibi modern tam yığın teknolojilerle birleştirilmesi, operasyonel pürüzleri azaltırken dağıtım boru hatlarının güvenilirliğini %40 oranında artırmaktadır."
    },
    {
      q: "Açık kaynaklı yazılım iş birliklerinin temel sütunları nelerdir?",
      a: "Açık kaynaklı yazılım iş birliklerinin temel sütunları şeffaflık, kapsayıcı eğitim ve hızlı kod entegrasyonudur. Geliştirici topluluklarının açık kaynaklı kütüphaneler üretmesi, kurumsal sınırlamaları aşarak kritik araçların yayına alınma döngülerini hızlandırmakta ve küresel teknoloji standartlarının yerelleştirilmesine imkan tanımaktadır."
    },
    {
      q: "Uğurcan Uzunkaya ile nasıl iletişime geçebilirim?",
      a: "Web sitesi üzerindeki iletişim formunu doldurarak Uğurcan Uzunkaya ile kolayca iletişime geçebilirsiniz. Alternatif olarak, geliştirici sistemleri ve otonom yazılım aracıları doğrudan /api/send-email uç noktasına HTTP POST isteği göndererek mesaj iletebilirler."
    }
  ]
};

export function FaqTab({ lang }: FaqTabProps) {
  const { faqs, loading } = useDynamicContent();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleIndex = (index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const getFaqItems = (): FaqItem[] => {
    if (faqs && faqs.length > 0) {
      return faqs.map((f) => ({
        q: lang === "tr" ? f.question_tr : f.question_en,
        a: lang === "tr" ? f.answer_tr : f.answer_en,
        links: parseLinks(f.links)
      }));
    }
    return lang === "tr" ? FAQ_DATA.tr : FAQ_DATA.en;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 space-y-6 animate-fade-in text-left">
        <div className="h-10 w-48 bg-muted animate-pulse rounded-lg" />
        <div className="h-4 w-96 bg-muted animate-pulse rounded-lg mt-2" />
        <div className="space-y-4 pt-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 w-full bg-muted/65 animate-pulse rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const items = getFaqItems();

  return (
    <div className="max-w-7xl mx-auto py-6 space-y-6 animate-fade-in text-left">
      <div className="border-b border-border/40 pb-4">
        <h3 className="text-3xl font-extrabold text-brand-primary">
          {lang === "tr" ? "Sıkça Sorulan Sorular" : "Frequently Asked Questions"}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {lang === "tr"
            ? "Uğurcan Uzunkaya'nın çalışmaları, entegrasyonları ve iş birlikleri hakkında detaylar."
            : "Information regarding Uğurcan Uzunkaya's engineering, integrations, and philosophies."}
        </p>
      </div>

      <div className="space-y-4">
        {items.map((item, idx) => {
          const isOpen = activeIndex === idx;
          return (
            <div
              key={idx}
              className="bg-card/45 backdrop-blur-md border border-border/40 rounded-2xl overflow-hidden transition-all duration-300 hover:border-brand-primary/40"
            >
              <button
                onClick={() => toggleIndex(idx)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between p-5 text-left font-semibold text-foreground hover:text-brand-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary/45 rounded-t-2xl"
              >
                <span>{item.q}</span>
                <span className={`text-lg font-mono transform transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}>
                  ＋
                </span>
              </button>

              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? "max-h-[500px] border-t border-border/30 opacity-100" : "max-h-0 opacity-0 pointer-events-none"
                  }`}
              >
                <div className="p-5 text-sm text-muted-foreground leading-relaxed font-sans bg-muted/10 space-y-4">
                  <div>{item.a}</div>
                  {item.links && item.links.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-border/20">
                      {item.links.slice(0, 5).map((link, linkIdx) => (
                        <a
                          key={linkIdx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-primary hover:text-brand-primary/80 transition-colors border border-brand-primary/20 hover:border-brand-primary/40 rounded-full px-3 py-1 bg-brand-primary/5 hover:bg-brand-primary/10 shadow-sm"
                        >
                          <LinkIcon className="h-3 w-3" />
                          <span>{lang === "tr" ? link.name_tr || link.name_en : link.name_en || link.name_tr}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FaqTab;
